import { Button } from '@journ/ui/button'
import { Icons } from '@journ/ui/icons'
import { Sheet, SheetContent, SheetHeader } from '@journ/ui/sheet'
import { useMemo } from 'react'
import { useIntlayer } from 'react-intlayer'
import { useSelector } from '@/libs/state'
import { session$ } from '@/features/auth/store'
import { usePermission } from '@/features/permissions/hooks/use-permission'
import { useMembers } from '../hooks/use-members'
import { useDeptMemberIds } from '../hooks/use-dept-member-ids'
import { useTimeOffActions } from '../hooks/use-time-off-actions'
import { refreshTimeOffList } from '../store'
import { TimeOffRequestForm } from './time-off-request-form'
import type { TimeOffFormValues } from './time-off-request-form'
import { useMemberParams } from './use-member-params'

const TimeOffRequestSheet = () => {
	const content = useIntlayer('hr-page')
	const { createTimeOff, setParams } = useMemberParams()
	const { members } = useMembers(null, 'active')
	const { submitRequest } = useTimeOffActions()
	const user = useSelector(() => session$.user.get())

	const canViewAll = usePermission('hr.time-off.view-all')
	const canViewDept = usePermission('hr.time-off.view-dept')
	const canViewTeam = usePermission('hr.time-off.view-team')

	const isEmployee = !canViewAll && !canViewDept && !canViewTeam
	const isDeptLeader = !canViewAll && !canViewTeam && canViewDept

	const ownMember = useMemo(
		() => members.find(m => m.email === user?.email) ?? null,
		[members, user?.email],
	)

	const { memberIds: deptMemberIds } = useDeptMemberIds(isDeptLeader ? (ownMember?.id ?? null) : null)

	const visibleMembers = useMemo(() => {
		if (isEmployee) return ownMember ? [ownMember] : []
		if (isDeptLeader) return members.filter(m => deptMemberIds.includes(m.id))
		return members
	}, [isEmployee, isDeptLeader, ownMember, members, deptMemberIds])

	const showMember = !isEmployee

	const isOpen = Boolean(createTimeOff)
	const close = () => setParams({ createTimeOff: null })

	const handleSubmit = async (values: TimeOffFormValues) => {
		const member = isEmployee ? ownMember : visibleMembers.find(m => m.id === values.memberId)
		if (!member) return
		await submitRequest({
			memberId: member.id,
			memberName: member.fullName,
			type: values.type,
			startDate: values.startDate,
			endDate: values.endDate,
			halfDay: values.halfDay ?? null,
			reason: values.reason ?? null,
		})
		refreshTimeOffList()
		close()
	}

	return (
		<Sheet open={isOpen} onOpenChange={close}>
			<SheetContent stack title={content.timeOff.requestSheet.createTitle.value}>
				<SheetHeader className='mb-6 flex justify-between items-center flex-row'>
					<h2 className='text-xl'>{content.timeOff.requestSheet.createTitle}</h2>
					<Button size='icon' variant='ghost' onClick={close} className='p-0 m-0 size-auto hover:bg-transparent'>
						<Icons.Close className='size-5' />
					</Button>
				</SheetHeader>
				<TimeOffRequestForm
					members={visibleMembers}
					showMember={showMember}
					initialMemberId={isEmployee ? (ownMember?.id ?? '') : ''}
					onSubmit={handleSubmit}
					onCancel={close}
				/>
			</SheetContent>
		</Sheet>
	)
}

export { TimeOffRequestSheet }
